import { ACCESS_LEVELS } from '@rti/shared';
import type { User } from '@rti/shared';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const GA_MEASUREMENT_ID = 'G-0N9QY0E27S';
export const GA_PRODUCTION_HOSTS = ['app.resinsmart.ai'];
export const GA_OPT_OUT_KEY = 'rs_ga_opt_out';
const GA_DISABLE_FLAG = `ga-disable-${GA_MEASUREMENT_ID}`;

const PII_KEY = /email|name|phone|address|password/i;

const TIER_LABELS: Record<number, string> = {
  0: 'lapsed',
  [ACCESS_LEVELS.BASE]: 'base',
  [ACCESS_LEVELS.COMPOUND]: 'compound',
  [ACCESS_LEVELS.PRIME]: 'prime',
  [ACCESS_LEVELS.EMPLOYEE]: 'employee',
};

export const isGoogleAnalyticsHost = (hostname: string = window.location.hostname): boolean =>
  GA_PRODUCTION_HOSTS.includes(hostname.toLowerCase());

export const isGoogleAnalyticsOptedOut = (): boolean => {
  try {
    return localStorage.getItem(GA_OPT_OUT_KEY) === '1';
  } catch {
    return false;
  }
};

export const optOutOfGoogleAnalytics = (): void => {
  if (typeof window === 'undefined') return;
  (window as unknown as Record<string, unknown>)[GA_DISABLE_FLAG] = true;
  try {
    localStorage.setItem(GA_OPT_OUT_KEY, '1');
  } catch {
    return;
  }
};

const isActive = (): boolean =>
  typeof window !== 'undefined' && typeof window.gtag === 'function' && !isGoogleAnalyticsOptedOut();

export const initGoogleAnalytics = (hostname?: string): boolean => {
  if (typeof window === 'undefined' || !isGoogleAnalyticsHost(hostname)) return false;
  if (isGoogleAnalyticsOptedOut()) {
    (window as unknown as Record<string, unknown>)[GA_DISABLE_FLAG] = true;
    return false;
  }
  if (isActive()) return true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID);

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  return true;
};

export const accountTypeFor = (user: User): 'employee' | 'trial' | 'customer' | 'lapsed' => {
  if (user.effective_membership_id === ACCESS_LEVELS.EMPLOYEE) return 'employee';
  if (user.on_free_trial) return 'trial';
  if (user.effective_membership_id === 0) return 'lapsed';
  return 'customer';
};

export const setGoogleAnalyticsUser = (user: User | null): void => {
  if (user && accountTypeFor(user) === 'employee') {
    optOutOfGoogleAnalytics();
    return;
  }
  if (!isActive()) return;
  if (!user) {
    window.gtag!('set', { user_id: null });
    window.gtag!('set', 'user_properties', { company_id: null, membership_tier: null, account_type: null });
    return;
  }
  window.gtag!('set', { user_id: String(user.user_id) });
  window.gtag!('set', 'user_properties', {
    company_id: user.company_id != null ? String(user.company_id) : null,
    membership_tier: TIER_LABELS[user.effective_membership_id ?? 1] ?? 'base',
    account_type: accountTypeFor(user),
  });
};

export const sanitizeEventName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/^[^a-z]+/, '')
    .slice(0, 40);

export const sanitizeEventParams = (params: Record<string, unknown> = {}): Record<string, string | number | boolean> => {
  const clean: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (PII_KEY.test(key) || value === undefined || value === null) continue;
    if (typeof value === 'number' || typeof value === 'boolean') {
      clean[sanitizeEventName(key)] = value;
    } else if (typeof value === 'string') {
      clean[sanitizeEventName(key)] = value.slice(0, 100);
    }
  }
  return clean;
};

export const trackGoogleAnalyticsEvent = (name: string, params: Record<string, unknown> = {}): void => {
  if (!isActive()) return;
  const eventName = sanitizeEventName(name);
  if (!eventName) return;
  window.gtag!('event', eventName, sanitizeEventParams(params));
};
